import { useCallback, useEffect, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { MicOff, X } from "lucide-react";

type MicDisabledBannerProps = {
    meetingClient: RealtimeKitClient | null;
    /** Observadores entram sem áudio por padrão — não exibir o aviso. */
    hideForObserver?: boolean;
};

const MicDisabledBanner: React.FC<MicDisabledBannerProps> = ({
    meetingClient,
    hideForObserver = false,
}) => {
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [isEnabling, setIsEnabling] = useState(false);

    const syncAudioState = useCallback(() => {
        if (!meetingClient?.self) return;
        setAudioEnabled(meetingClient.self.audioEnabled ?? false);
    }, [meetingClient]);

    useEffect(() => {
        if (!meetingClient?.self) return;

        syncAudioState();

        const handleAudioUpdate = () => {
            syncAudioState();
            if (meetingClient.self.audioEnabled) {
                setDismissed(false);
            }
        };

        meetingClient.self.on("audioUpdate", handleAudioUpdate);

        const participants = meetingClient.participants?.joined;
        participants?.on("audioUpdate", handleAudioUpdate);

        return () => {
            meetingClient.self.removeListener("audioUpdate", handleAudioUpdate);
            participants?.off("audioUpdate", handleAudioUpdate);
        };
    }, [meetingClient, syncAudioState]);

    const handleEnableMic = async () => {
        if (!meetingClient?.self || isEnabling) return;

        setIsEnabling(true);
        try {
            await meetingClient.self.enableAudio();
            setDismissed(false);
            syncAudioState();
        } catch (error) {
            console.error("Erro ao ligar microfone:", error);
        } finally {
            setIsEnabling(false);
        }
    };

    if (hideForObserver || !meetingClient || audioEnabled || dismissed) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            data-tour="mic-banner"
            className="flex-shrink-0 z-[70] flex items-center gap-1.5 border-b border-slate-700/60 bg-slate-900/85 px-2 py-1 text-slate-300 sm:gap-2 sm:px-3"
        >
            <MicOff className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
            <p className="min-w-0 flex-1 truncate text-[11px] leading-tight sm:text-xs">
                <span className="sm:hidden">Microfone desligado.</span>
                <span className="hidden sm:inline">
                    Seu microfone está desligado. Os outros não conseguem ouvir você.
                </span>
            </p>
            <button
                type="button"
                onClick={handleEnableMic}
                disabled={isEnabling}
                className="shrink-0 text-[11px] font-medium text-blue-400 underline-offset-2 transition-colors hover:text-blue-300 hover:underline disabled:opacity-60 sm:text-xs"
            >
                {isEnabling ? "Ligando…" : "Ligar"}
            </button>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="shrink-0 rounded p-0.5 text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300"
                aria-label="Fechar aviso de microfone"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default MicDisabledBanner;
